import { NameDto } from '@/api/Common/Types'
import { DocumentStatus } from 'api/Documents/Types/ClientDocument'
import { CountryDto } from 'api/Identity/Types/Country'

export interface TicketDetailsDocumentDataDto {
  document: {
    id: string
    name: string

    status: DocumentStatus
  }
  country: {
    id: string | null
    name: string | null
  }
  expiryDate?: string
  firstName?: string | null
  lastName: string | null
  notes: string | null
  placeOfBirth: string | null
  documentReference?: null
  city?: null
  postalCode?: null | string
  state?: null | string
  street?: null | string
  dateOfIssue: string | null
  dateOfBirth?: string | null
  countryId?: string | null
}

export interface TicketDetailsTransactionDataDto {
  transaction: {
    id: string
    name: string
    groupId?: string
    groupReferenceId?: string
  }
}

export interface TicketDetailsKycUpdateDataDto {
  kycUpdate: {
    clientId: string
    phoneNumber: string
    email: string
    country: string
    state: string
    city: string
    street: string
    streetHouseNo: string
    zipCode: string
    taxIdentificationNumber: string
    nationalityId: string
    kycTestId: string
    document: {
      id: string
      name: string
    }
  }
}

export const isDormantReactivationData = (
  object: TicketDetailsDataDto
): object is TicketDetailsKycUpdateDataDto => {
  return object && 'dormantUpdate' in object
}

export const isKycUpdateData = (
  object: TicketDetailsDataDto
): object is TicketDetailsKycUpdateDataDto => {
  return object && 'kycUpdate' in object
}

export const isTransactionData = (
  object: TicketDetailsDataDto
): object is TicketDetailsTransactionDataDto => {
  return object && 'transaction' in object
}

export const isTransactionGroupData = (
  object?: TicketDetailsDataDto
): object is TicketDetailsTransactionDataDto => {
  return (
    !!object &&
    'transaction' in object &&
    'groupId' in object.transaction &&
    'groupReferenceId' in object.transaction
  )
}

export const isDocumentData = (
  object: TicketDetailsDataDto
): object is TicketDetailsDocumentDataDto => {
  return object && 'document' in object
}

export type TicketDetailsDataDto =
  | TicketDetailsDocumentDataDto
  | TicketDetailsTransactionDataDto
  | TicketDetailsKycUpdateDataDto
  | {
      previousStatus?: NameDto
      nextStatus?: NameDto
      middleName?: string
    }

export interface TicketDetailsDto {
  data: TicketDetailsDataDto
  id: string
  kind:
    | 'transaction'
    | 'client_registration'
    | 'document'
    | 'kyc_update'
    | 'loyalty_ticket_tier_status_changed'
    | string
  lead?: {
    country: {
      id: string
      name: string
    }
    id: string
    name: string
    phoneNumber: string
    tickmillCompnay: {
      id: number
      name: string
    }
  }
  number: number
  description: string
  comment: string
  assignee: {
    id: string
    name: string
  } | null
  client: {
    id: string
    name: string | null
    country: CountryDto
    tickmillCompany: {
      id: number
      name: string
    }
  } | null
  source?: {
    id: number
    name: string | null
  }
  status: {
    name: string | null
    id: number
  }
  type: {
    id: number
    name: string
  }
  departments: {
    id: number
    name: string
  }[]
  viewType: {
    id: number
    name: string
  }
  relatesTo: TicketDetailsDto | null
}

export enum TicketTypeId {
  DOCUMENT = 1,
  CLIENT_REGISTRATION = 2,
  DEPOSIT = 3,
  WITHDRAWAL = 4,
  CLIENT_CLASSIFICATION = 5,
  INTRODUCING_BROKER_REGISTRATION = 6,
  TRANSLATION = 7,
  ADJUSTMENT = 8,
  WALLET_TRANSFER = 9,
  TRADING_ACCOUNT_TRANSFER = 10,
  UNKNOWN_TRANSACTION = 11,
  PAYMENT_AGENT_DEPOSIT = 12,
  PAYMENT_AGENT_WITHDRAWAL = 13,
  IB_DEPOSIT = 14,
  IB_WITHDRAWAL = 15,
  KYC_Update,
  Appropriateness_Test_Failed,
  Client_Screening,
  Classification_Document,
  Company_Document_Translation,
  Identification_Translation,
  Proof_Of_Address_Translation,
  Pending_World_Check,
  Callback_Request,
  Dormant_Reactivation,
  TRADING_ACCOUNT_TRANSFER_IN = 26,
}
